import { normalizePathForComparison, getCredentialVaultNavSideFromPath } from '../jsx/layouts/nav/moduleNav';

/**
 * Context-aware "Add" destination for the universal navbar plus button.
 * @param {string} pathname
 * @param {string} [search]
 * @param {object|null} [locationState]
 * @returns {{ to: string, title: string } | null}
 */
export function getUniversalAddAction(pathname, search = '', locationState = null) {
  const path = normalizePathForComparison(pathname).toLowerCase();
  if (!path) return null;

  // Already on a create/edit form — hide Add
  if (
    path.includes('/add') ||
    path.includes('/create') ||
    path.includes('/upload') ||
    path.includes('/edit') ||
    path.endsWith('/register') ||
    path === '/login' ||
    path === '/dashboard'
  ) {
    return null;
  }

  // Tasks module (list, details, worksheet, board, performance)
  if (
    path === '/task/tasklist' ||
    path.startsWith('/task/taskdetails') ||
    path === '/task/worksheet' ||
    path === '/task/taskboard' ||
    path.startsWith('/task/performance')
  ) {
    return { to: '/task/addtask', title: 'New Task' };
  }

  // Project tasks / dashboard — create task or project depending on context
  if (path === '/task/projecttasklist') {
    return { to: '/task/addtask', title: 'New Task' };
  }
  if (path === '/task/projectdashboard') {
    return { to: '/task/addproject', title: 'New Project' };
  }

  // Projects
  if (path === '/task/projectlist' || path.startsWith('/task/projectdetails')) {
    return { to: '/task/addproject', title: 'New Project' };
  }

  // Forum
  if (path === '/forum/dashboard' || path === '/forum/list' || path.startsWith('/forum/detail')) {
    return { to: '/forum/create', title: 'Start conversation' };
  }

  // Document classifications
  if (path === '/documents/documentclassifications') {
    return {
      to: '/documents/addDocumentClassification',
      title: 'New Classification',
    };
  }

  // Documents (browse / shared / favorites / dashboard / details)
  if (
    path === '/documents/mydocuments' ||
    path.startsWith('/documents/folder/') ||
    path === '/documents/shared' ||
    path === '/documents/favorites' ||
    path === '/documents/dashboard' ||
    path.startsWith('/documents/details/')
  ) {
    return { to: '/documents/upload', title: 'Upload Document' };
  }

  // Credential vault
  if (path.startsWith('/credential-vault/')) {
    if (path.includes('vaultsettings')) return null;
    const side = getCredentialVaultNavSideFromPath(pathname, search, locationState);
    if (side === 'personal') {
      return {
        to: '/credential-vault/addCredential?scope=personal',
        title: 'Add credential',
      };
    }
    if (
      path.includes('credentialvaultlist') ||
      path.includes('personalvaultlist') ||
      path.includes('credentialdetail')
    ) {
      return {
        to: '/credential-vault/addCredential',
        title: 'Add credential',
      };
    }
  }

  // Users
  if (path === '/settings/userlist' || path.startsWith('/settings/user')) {
    return { to: '/settings/adduser', title: 'Add User' };
  }

  // Org settings lists
  if (path === '/settings/branchlist') {
    return { to: '/settings/addBranch', title: 'Add Branch' };
  }
  if (path === '/settings/departmentlist') {
    return { to: '/settings/addDepartment', title: 'Add Department' };
  }
  if (path === '/settings/subdepartmentlist') {
    return { to: '/settings/addSubDepartment', title: 'Add Sub-Department' };
  }
  if (path === '/settings/designationlist') {
    return { to: '/settings/addDesignation', title: 'Add Designation' };
  }

  // Staff
  if (path === '/staff/stafflist' || (path.startsWith('/staff/') && !path.includes('/add'))) {
    // Detail pages still get Add Staff (new staff record)
    if (path.includes('/address/') || path.includes('/contact/') || path.includes('/employment/') || path.includes('/document/')) {
      return null;
    }
    return { to: '/staff/addStaff', title: 'Add Staff' };
  }

  // Vehicles
  if (path === '/vehicle/vehiclelist' || (path.startsWith('/vehicle/') && !path.includes('/add'))) {
    if (
      path.includes('/document/') ||
      path.includes('/insurance/') ||
      path.includes('/registration/')
    ) {
      return null;
    }
    return { to: '/vehicle/addVehicle', title: 'Add Vehicle' };
  }

  return null;
}
